import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../store";
import { addSmallCraft, removeSmallCraft } from "../../../store/features/shipBuilderSlice";
import { SmallCraft } from "../../../types/ship";
import { selectEffectiveDriveRatings, selectCombatThrustInfo } from "../../../store/selectors/derivedSelectors";



/**
 * Component for assigning user-defined small craft to the ship in the finalise design step
 * Uses accurate capacity data from Step 9 Optional Systems
 */
export const SmallCraftAssignment: React.FC = () => {
  const dispatch = useAppDispatch();
  const optionalSystems = useAppSelector((state) => state.optionalSystems);
  const smallCraft = useAppSelector((state) => state.shipBuilder.smallCraft || []);
  const effectiveDriveRatings = useAppSelector(selectEffectiveDriveRatings);
  const combatThrustInfo = useAppSelector(selectCombatThrustInfo);

  // Get craft capacity systems from optional systems
  const dockingClamps = optionalSystems.external.filter(
    (system) => system.subtype === "dockingClamp"
  );
  const dockingSpaces = optionalSystems.internal.filter(
    (system) => system.subtype === "internalDockingSpace"
  );
  const fullHangars = optionalSystems.internal.filter(
    (system) => system.subtype === "internalFullHangar"
  );

  // Calculate total capacity from all craft systems
  const dockingClampDetails = dockingClamps.map(clamp => {
    const clampType = clamp.properties?.clampType || "typeI";
    let capacity = 0;
    
    // Use the correct capacity based on clamp type
    switch (clampType) {
      case "typeI":
        capacity = 30;
        break;
      case "typeII":
        capacity = 99;
        break;
      case "typeIII":
        capacity = 300;
        break;
      case "typeIV":
        capacity = 2000;
        break;
      case "typeV":
        capacity = 999999; // Effectively unlimited
        break;
      default:
        capacity = 30;
    }
    
    return { clampType, capacity };
  });

  // Check if any Type V clamps exist (unlimited capacity)
  const hasUnlimitedClamps = dockingClampDetails.some(detail => detail.clampType === "typeV");
  
  // Calculate total capacity - if any Type V clamps exist, show unlimited
  const totalDockingClampCapacity = hasUnlimitedClamps 
    ? 999999 // Show unlimited if any Type V clamps
    : (dockingClampDetails || []).reduce((total, detail) => total + detail.capacity, 0);

  const totalDockingSpaceCapacity = (dockingSpaces || []).reduce((total, space) => {
    // Use vessel capacity (original vessel size) instead of space tonnage for total capacity
    const vesselCapacity = (space.properties?.vesselCapacity as number) || space.tonnage || 0;
    return total + vesselCapacity;
  }, 0);

  const totalFullHangarCapacity = (fullHangars || []).reduce((total, hangar) => {
    // Full Hangar capacity is half its tonnage (for maintenance/repair space)
    return total + ((hangar.tonnage || 0) / 2);
  }, 0);



  // State for new craft form
  const [newCraft, setNewCraft] = useState<Omit<SmallCraft, 'id'>>({
    type: "",
    size: 0,
    quantity: 1,
    details: "",
    dockingLocation: "internal",
    assignedSystem: "",
  });

  // Calculate used capacity by location
  const usedExternalCapacity = (smallCraft || [])
    .filter(craft => craft.dockingLocation === 'external')
    .reduce((total, craft) => total + (craft.size * craft.quantity), 0);
    
  const usedInternalCapacity = (smallCraft || [])
    .filter(craft => craft.dockingLocation === 'internal')
    .reduce((total, craft) => total + (craft.size * craft.quantity), 0);
    


  // Generate system options based on docking location
  const getSystemOptions = () => {
    if (newCraft.dockingLocation === 'external') {
      return dockingClamps.map((clamp, index) => {
        const clampType = clamp.properties?.clampType || "typeI";
        let capacity = 0;
        let typeLabel = "";
        
        switch (clampType) {
          case "typeI": capacity = 30; typeLabel = "Type I"; break;
          case "typeII": capacity = 99; typeLabel = "Type II"; break;
          case "typeIII": capacity = 300; typeLabel = "Type III"; break;
          case "typeIV": capacity = 2000; typeLabel = "Type IV"; break;
          case "typeV": capacity = 999999; typeLabel = "Type V"; break;
          default: capacity = 30; typeLabel = "Type I";
        }
        
        const usedInThisClamp = (smallCraft || [])
          .filter(craft => craft.assignedSystem === `clamp-${index}`)
          .reduce((total, craft) => total + (craft.size * craft.quantity), 0);
        
        const availableInThisClamp = clampType === "typeV" ? "Unlimited" : `${Math.max(0, capacity - usedInThisClamp)} tons`;
        
        return {
          id: `clamp-${index}`,
          name: `Docking Clamp ${index + 1} (${typeLabel})`,
          capacity: availableInThisClamp,
          used: usedInThisClamp,
          totalCapacity: capacity
        };
      });
    } else {
      const spaceOptions = dockingSpaces.map((space, index) => {
        // Use vessel capacity (original vessel size) instead of space tonnage for docking capacity
        const vesselCapacity = (space.properties?.vesselCapacity as number) || space.tonnage || 0;
        const usedInThisSpace = (smallCraft || [])
          .filter(craft => craft.assignedSystem === `space-${index}`)
          .reduce((total, craft) => total + (craft.size * craft.quantity), 0);
        
        return {
          id: `space-${index}`,
          name: `Docking Space ${index + 1} (${vesselCapacity} tons)`,
          capacity: `${Math.max(0, vesselCapacity - usedInThisSpace)} tons`,
          used: usedInThisSpace,
          totalCapacity: vesselCapacity
        };
      });
      
      const hangarOptions = fullHangars.map((hangar, index) => {
        const hangarTonnage = hangar.tonnage || 0;
        const hangarCapacity = hangarTonnage / 2;
        const usedInThisHangar = (smallCraft || [])
          .filter(craft => craft.assignedSystem === `hangar-${index}`)
          .reduce((total, craft) => total + (craft.size * craft.quantity), 0);
        
        return {
          id: `hangar-${index}`,
          name: `Full Hangar ${index + 1}`,
          capacity: `${Math.max(0, hangarCapacity - usedInThisHangar)} tons`,
          used: usedInThisHangar,
          totalCapacity: hangarCapacity
        };
      });
      
      return [...spaceOptions, ...hangarOptions];
    }
  };

  const systemOptions = getSystemOptions();

  const handleAddCraft = () => {
    if (newCraft.type && newCraft.size > 0 && newCraft.quantity > 0) {
      const craft: SmallCraft = {
        ...newCraft,
        id: Date.now().toString(),
      };
      dispatch(addSmallCraft(craft));
      setNewCraft({
        type: "",
        size: 0,
        quantity: 1,
        details: "",
        dockingLocation: "internal",
        assignedSystem: "",
      });
    }
  };

  const handleRemoveCraft = (id: string) => {
    dispatch(removeSmallCraft(id));
  };

  const canAddCraft = newCraft.type && newCraft.size > 0 && newCraft.quantity > 0 && newCraft.assignedSystem && 
    (() => {
      const craftTonnage = newCraft.size * newCraft.quantity;
      const selectedSystem = systemOptions.find(option => option.id === newCraft.assignedSystem);
      
      if (!selectedSystem) return false;
      
      if (selectedSystem.capacity === "Unlimited") return true;
      
      const availableCapacity = parseFloat(selectedSystem.capacity.replace(" tons", ""));
      return craftTonnage <= availableCapacity;
    })();

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Small Craft Assignment
      </h3>

      {/* Craft Capacity Information */}
      <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
          Craft Capacity Summary
        </h4>
        
        {/* External vs Internal Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          {/* External Capacity */}
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
            <h5 className="text-md font-semibold text-blue-800 dark:text-blue-200 mb-2">External Capacity</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-blue-600 dark:text-blue-300">Docking Clamps:</span>
                <span className="text-blue-900 dark:text-blue-100">
                  {dockingClamps.length} ({hasUnlimitedClamps ? "Unlimited" : `${totalDockingClampCapacity} tons`})
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-blue-600 dark:text-blue-300">Used External:</span>
                <span className="text-blue-900 dark:text-blue-100">{usedExternalCapacity} tons</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-blue-600 dark:text-blue-300">Available External:</span>
                <span className="text-blue-900 dark:text-blue-100 font-semibold">
                  {hasUnlimitedClamps ? "Unlimited" : `${Math.max(0, totalDockingClampCapacity - usedExternalCapacity)} tons`}
                </span>
              </div>
            </div>
          </div>

          {/* Internal Capacity */}
          <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg p-3">
            <h5 className="text-md font-semibold text-purple-800 dark:text-purple-200 mb-2">Internal Capacity</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-purple-600 dark:text-purple-300">Docking Spaces:</span>
                <span className="text-purple-900 dark:text-purple-100">{dockingSpaces.length} ({totalDockingSpaceCapacity} tons)</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-purple-600 dark:text-purple-300">Full Hangars:</span>
                <span className="text-purple-900 dark:text-purple-100">{fullHangars.length} ({totalFullHangarCapacity} tons)</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-purple-600 dark:text-purple-300">Used Internal:</span>
                <span className="text-purple-900 dark:text-purple-100">{usedInternalCapacity} tons</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-purple-600 dark:text-purple-300">Available Internal:</span>
                <span className="text-purple-900 dark:text-purple-100 font-semibold">
                  {Math.max(0, totalDockingSpaceCapacity + totalFullHangarCapacity - usedInternalCapacity)} tons
                </span>
              </div>
            </div>
          </div>
        </div>

              {/* Craft Count Summary */}
      <div className="border-t border-green-200 dark:border-green-700 pt-4">
        <div className="text-sm">
          <span className="font-medium text-green-600 dark:text-green-300">Total Craft Count: </span>
          <span className="text-green-900 dark:text-green-100">{smallCraft.length}</span>
        </div>
      </div>

      {/* Drive Performance Impact */}
      {effectiveDriveRatings.hasExternalSmallCraft && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-yellow-800 mb-2">
            ⚠️ Drive Performance Impact
          </h4>
          <div className="text-sm text-yellow-700 space-y-2">
          <div>
              <span className="font-medium">External Small Craft Tonnage: </span>
              <span>{effectiveDriveRatings.externalSmallCraftTonnage} tons</span>
          </div>
          <div>
              <span className="font-medium">Effective Total Tonnage: </span>
              <span>{effectiveDriveRatings.effectiveTonnage} tons</span>
            </div>
            {effectiveDriveRatings.baseManeuverRating > 0 && (
              <div>
                <span className="font-medium">Maneuver Drive: </span>
                <span>{effectiveDriveRatings.baseManeuverRating} → {effectiveDriveRatings.effectiveManeuverRating}</span>
              </div>
            )}
            {effectiveDriveRatings.baseJumpRating > 0 && (
              <div>
                <span className="font-medium">Jump Drive: </span>
                <span>{effectiveDriveRatings.baseJumpRating} → {effectiveDriveRatings.effectiveJumpRating}</span>
              </div>
            )}
            {effectiveDriveRatings.baseReactionRating > 0 && (
              <div>
                <span className="font-medium">Reaction Drive: </span>
                <span>{effectiveDriveRatings.baseReactionRating} → {effectiveDriveRatings.effectiveReactionRating}</span>
              </div>
            )}
            {combatThrustInfo && (
              <div>
                <span className="font-medium">Combat Thrust: </span>
                <span>{combatThrustInfo.maxThrust} → {combatThrustInfo.effectiveThrust}</span>
              </div>
            )}
            <div className="text-xs text-yellow-600 mt-2">
              <strong>Note:</strong> Externally mounted small craft add their tonnage to the ship's total mass for thrust and jump drive calculations.
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Add New Small Craft Form */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Add Small Craft
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Small Craft Type */}
          <div>
            <label htmlFor="craft-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Craft Type *
            </label>
            <input
              id="craft-type"
              type="text"
              value={newCraft.type}
              onChange={(e) => setNewCraft({ ...newCraft, type: e.target.value })}
              placeholder="e.g., Shuttle, Fighter, Cargo Pod"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          {/* Size */}
          <div>
            <label htmlFor="craft-size" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Size (tons) *
            </label>
            <input
              id="craft-size"
              type="number"
              min="1"
              step="1"
              value={newCraft.size || ""}
              onChange={(e) => setNewCraft({ ...newCraft, size: parseInt(e.target.value) || 0 })}
              placeholder="1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          {/* Quantity */}
          <div>
            <label htmlFor="craft-quantity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quantity *
            </label>
            <input
              id="craft-quantity"
              type="number"
              min="1"
              value={newCraft.quantity || ""}
              onChange={(e) => setNewCraft({ ...newCraft, quantity: parseInt(e.target.value) || 1 })}
              placeholder="1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          {/* Docking Location */}
          <div>
            <label htmlFor="craft-docking-location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Docking Location *
            </label>
            <select
              id="craft-docking-location"
              value={newCraft.dockingLocation}
              onChange={(e) => {
                setNewCraft({ 
                  ...newCraft, 
                  dockingLocation: e.target.value as 'external' | 'internal',
                  assignedSystem: "" // Reset system selection when location changes
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="internal">Internal</option>
              <option value="external">External</option>
            </select>
        </div>

          {/* Assigned System */}
              <div>
            <label htmlFor="craft-assigned-system" className="block text-sm font-medium text-gray-700 mb-2">
              Assign To System *
            </label>
            <select
              id="craft-assigned-system"
              value={newCraft.assignedSystem}
              onChange={(e) => setNewCraft({ ...newCraft, assignedSystem: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={systemOptions.length === 0}
            >
              <option value="">Select system...</option>
              {systemOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name} ({option.capacity})
                </option>
              ))}
            </select>
          </div>

          {/* Other Details */}
              <div>
            <label htmlFor="craft-details" className="block text-sm font-medium text-gray-700 mb-2">
              Other Details
            </label>
            <input
              id="craft-details"
              type="text"
              value={newCraft.details}
              onChange={(e) => setNewCraft({ ...newCraft, details: e.target.value })}
              placeholder="e.g., Armed, Luxury, Cargo"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Add Button */}
        <div className="mt-4">
          <button
            onClick={handleAddCraft}
            disabled={!canAddCraft}
            className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
              canAddCraft
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Add Small Craft
          </button>
        </div>

        {/* Validation Messages */}
        {newCraft.size > 0 && newCraft.quantity > 0 && newCraft.assignedSystem && (() => {
          const craftTonnage = newCraft.size * newCraft.quantity;
          const selectedSystem = systemOptions.find(option => option.id === newCraft.assignedSystem);
          
          if (!selectedSystem) return false;
          
          if (selectedSystem.capacity === "Unlimited") return false;
          
          const availableCapacity = parseFloat(selectedSystem.capacity.replace(" tons", ""));
          return craftTonnage > availableCapacity;
        })() && (
          <div className="mt-3 text-red-600 text-sm">
            ⚠️ Insufficient capacity in {systemOptions.find(option => option.id === newCraft.assignedSystem)?.name}: 
            {(newCraft.size * newCraft.quantity).toFixed(1)} tons required, 
            {systemOptions.find(option => option.id === newCraft.assignedSystem)?.capacity} available
          </div>
        )}
      </div>

      {/* Assigned Small Craft List */}


      {/* Capacity Systems Details */}
      {(dockingClamps.length > 0 || dockingSpaces.length > 0 || fullHangars.length > 0) && (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-gray-800 mb-3">
            Craft Capacity Systems
        </h4>
          <div className="space-y-3">
            {dockingClamps.map((clamp, index) => {
              const clampType = clamp.properties?.clampType || "typeI";
              let capacity = 0;
              let typeLabel = "";
              
              // Get capacity and label based on clamp type
              switch (clampType) {
                case "typeI":
                  capacity = 30;
                  typeLabel = "Type I";
                  break;
                case "typeII":
                  capacity = 99;
                  typeLabel = "Type II";
                  break;
                case "typeIII":
                  capacity = 300;
                  typeLabel = "Type III";
                  break;
                case "typeIV":
                  capacity = 2000;
                  typeLabel = "Type IV";
                  break;
                case "typeV":
                  capacity = 999999;
                  typeLabel = "Type V (Unlimited)";
                  break;
                default:
                  capacity = 30;
                  typeLabel = "Type I";
              }
              
              // Get assigned craft for this clamp
              const assignedCraft = smallCraft.filter(craft => craft.assignedSystem === `clamp-${index}`);
              const usedCapacity = assignedCraft.reduce((total, craft) => total + (craft.size * craft.quantity), 0);
              
              return (
                <div key={`clamp-${index}`} className="bg-white p-3 rounded border">
                  <div className="font-medium text-gray-900">Docking Clamp {index + 1} ({typeLabel})</div>
                  <div className="text-sm text-gray-600">
                    Capacity: {capacity === 999999 ? "Unlimited" : `${capacity} tons`}
                    {usedCapacity > 0 && (
                      <span className="text-blue-600 ml-2">• Used: {usedCapacity} tons</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    External mounting for auxiliary craft
                  </div>
                  {assignedCraft.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">Assigned Craft:</div>
                      {assignedCraft.map((craft, craftIndex) => (
                        <div key={craftIndex} className="flex items-center justify-between text-xs text-gray-600">
                          <div>
                            • {craft.type} ({craft.size} tons × {craft.quantity})
                            {craft.details && <span className="text-gray-500 italic"> - {craft.details}</span>}
                          </div>
                          <button
                            onClick={() => handleRemoveCraft(craft.id)}
                            className="ml-2 px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {dockingSpaces.map((space, index) => {
              // Use vessel capacity (original vessel size) instead of space tonnage for docking capacity
              const vesselCapacity = (space.properties?.vesselCapacity as number) || space.tonnage || 0;
              const assignedCraft = smallCraft.filter(craft => craft.assignedSystem === `space-${index}`);
              const usedCapacity = assignedCraft.reduce((total, craft) => total + (craft.size * craft.quantity), 0);
              
              return (
                <div key={`space-${index}`} className="bg-white p-3 rounded border">
                  <div className="font-medium text-gray-900">Docking Space {index + 1} (Space: {space.tonnage} tons)</div>
                  <div className="text-sm text-gray-600">
                    Capacity: {vesselCapacity} tons
                    {usedCapacity > 0 && (
                      <span className="text-purple-600 ml-2">• Used: {usedCapacity} tons</span>
                    )}
                  </div>
                  {assignedCraft.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">Assigned Craft:</div>
                      {assignedCraft.map((craft, craftIndex) => (
                        <div key={craftIndex} className="flex items-center justify-between text-xs text-gray-600">
                          <div>
                            • {craft.type} ({craft.size} tons × {craft.quantity})
                            {craft.details && <span className="text-gray-500 italic"> - {craft.details}</span>}
                          </div>
                          <button
                            onClick={() => handleRemoveCraft(craft.id)}
                            className="ml-2 px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {fullHangars.map((hangar, index) => {
              const hangarTonnage = hangar.tonnage || 0;
              const capacity = hangarTonnage / 2; // Full Hangar capacity is half its tonnage
              const assignedCraft = smallCraft.filter(craft => craft.assignedSystem === `hangar-${index}`);
              const usedCapacity = assignedCraft.reduce((total, craft) => total + (craft.size * craft.quantity), 0);
              
              return (
                <div key={`hangar-${index}`} className="bg-white p-3 rounded border">
                  <div className="font-medium text-gray-900">Full Hangar {index + 1}</div>
                  <div className="text-sm text-gray-600">
                    Size: {hangarTonnage} tons • Capacity: {capacity} tons
                    {usedCapacity > 0 && (
                      <span className="text-purple-600 ml-2">• Used: {usedCapacity} tons</span>
                    )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                    Allows repairs/maintenance of craft on board (50% capacity for equipment)
              </div>
                  {assignedCraft.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">Assigned Craft:</div>
                      {assignedCraft.map((craft, craftIndex) => (
                        <div key={craftIndex} className="flex items-center justify-between text-xs text-gray-600">
                          <div>
                            • {craft.type} ({craft.size} tons × {craft.quantity})
                            {craft.details && <span className="text-gray-500 italic"> - {craft.details}</span>}
              </div>
                          <button
                            onClick={() => handleRemoveCraft(craft.id)}
                            className="ml-2 px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            Remove
                          </button>
            </div>
          ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
      )}
    </div>
  );
};
